<template>
    <Button unstyled :class="classes" :disabled="disabled" @click="$emit('click', $event)">
        <span v-if="$slots.default" class="calc-btn__label"><slot /></span>
        <span v-else-if="label" class="calc-btn__label">{{ $t(label) }}</span>
        <CalcIcon v-if="iconName" :name="iconName" :color="iconColor" />
    </Button>
</template>

<script>
import Button from 'primevue/button'
import CalcIcon from './CalcIcon.vue'

export default {
    name: 'CalcButton',

    components: { Button, CalcIcon },

    props: {
        label: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: 'primary',
            validator: (v) => ['primary', 'secondary', 'bare', 'download', 'ghost'].includes(v),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        iconName: {
            type: String,
            default: null,
        },
        iconColor: {
            type: String,
            default: 'currentColor',
        },
    },

    emits: ['click'],

    computed: {
        classes() {
            return {
                'calc-btn': true,
                [`calc-btn--${this.variant}`]: true,
            }
        },
    },
}
</script>

<style scoped>
.calc-btn.calc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    padding: 8px 20px;
    border-radius: 100px;
    font-family: 'TWK Lausanne', 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    transition:
        transform var(--calc-t-fast),
        background var(--calc-t-med),
        color var(--calc-t-med),
        border-color var(--calc-t-med),
        opacity var(--calc-t-med),
        box-shadow var(--calc-t-med);
    white-space: nowrap;
    border: 1px solid transparent;
    cursor: pointer;
}
.calc-btn.calc-btn:active {
    transform: scale(0.98);
}
.calc-btn.calc-btn:focus,
.calc-btn.calc-btn:focus-visible {
    outline: none;
}
.calc-btn.calc-btn .calc-btn__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.calc-btn.calc-btn--primary {
    background: var(--calc-ink);
    color: white;
    padding: 8px 12px 8px 20px;
    border-color: var(--calc-ink);
}
.calc-btn.calc-btn--primary:hover {
    background: rgb(33, 57, 57);
    border-color: rgb(33, 57, 57);
}
.calc-btn.calc-btn--primary[disabled] {
    background: rgba(4, 15, 15, 0.4);
    border-color: transparent;
    cursor: not-allowed;
    opacity: 0.7;
}
.calc-btn.calc-btn--secondary {
    background: white;
    color: var(--calc-ink);
    border: 1px solid var(--calc-ink);
}
.calc-btn.calc-btn--secondary:hover {
    background: var(--calc-ink);
    color: white;
}
.calc-btn.calc-btn--ghost {
    background: transparent;
    color: var(--calc-ink);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
}
.calc-btn.calc-btn--ghost:hover {
    opacity: 0.6;
}
.calc-btn.calc-btn--bare {
    background: transparent;
    color: var(--calc-ink);
    border: none;
    padding: 8px 20px;
}
.calc-btn.calc-btn--bare:hover {
    opacity: 0.6;
}
.calc-btn.calc-btn--download {
    background: var(--calc-green-accent);
    color: var(--calc-ink);
    padding: 8px 18px 8px 20px;
    border-color: var(--calc-green-accent);
}
.calc-btn.calc-btn--download:hover {
    background: rgb(63, 209, 152);
    border-color: rgb(63, 209, 152);
}
</style>
